import React, { useState, useContext } from "react";
import { Container, Row, Col, Form, FormGroup, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import "../styles/AdminLogin.css";
import loginImg from "../assets/images/login.png";
import userIcon from "../assets/images/user.png";
import { AuthContext } from "../context/AuthContext";
import { BASE_URL } from "../utils/config";

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(credentials.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!credentials.password) {
      newErrors.password = "Password is required";
    } else if (credentials.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCredentials((prevCredentials) => ({
      ...prevCredentials,
      [id]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: "" }));
    }
    if (error) setError(null);
  };

  const handleClick = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    dispatch({ type: "LOGIN_START" });

    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: credentials.email.trim(),
          password: credentials.password,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        setError(result.message || "Invalid email or password. Please try again.");
        dispatch({ type: "LOGIN_FAILURE", payload: result.message });
      } else {
        // Ensure token exists
        if (!result.token) {
          console.error("Login response missing token:", result);
          setError("Login failed: No token received from server");
          dispatch({ type: "LOGIN_FAILURE", payload: "No token received" });
          return;
        }

        // Check if user is admin - the role should be in the login response now
        if (result.role === 'admin') {
          const userPayload = { 
            ...result, 
            username: result.username || result.email?.split('@')[0] || 'Admin',
            role: 'admin',
            token: result.token // Explicitly ensure token is included
          };
          console.log("Storing admin user with token:", { ...userPayload, token: '***' });
          dispatch({ 
            type: "LOGIN_SUCCESS", 
            payload: userPayload
          });
          navigate("/admin/dashboard");
        } else {
          // Try to fetch user details to verify admin status
          try {
            const userRes = await fetch(`${BASE_URL}/users/${result.id}`, {
              headers: {
                "Authorization": result.token,
              },
            });
            
            if (userRes.ok) {
              const userData = await userRes.json();
              const actualUser = userData?.data || userData;
              
              if (actualUser && actualUser.role === 'admin') {
                const userPayload = { 
                  ...result, 
                  username: actualUser.username || result.username || result.email?.split('@')[0] || 'Admin',
                  role: 'admin',
                  token: result.token // Explicitly ensure token is included
                };
                console.log("Storing admin user with token:", { ...userPayload, token: '***' });
                dispatch({ 
                  type: "LOGIN_SUCCESS", 
                  payload: userPayload
                });
                navigate("/admin/dashboard");
              } else {
                setError("Access denied. Admin privileges required.");
                dispatch({ type: "LOGIN_FAILURE", payload: "Access denied. Admin privileges required." });
              }
            } else {
              setError("Access denied. Admin privileges required.");
              dispatch({ type: "LOGIN_FAILURE", payload: "Access denied. Admin privileges required." });
            }
          } catch (err) {
            setError("Access denied. Admin privileges required.");
            dispatch({ type: "LOGIN_FAILURE", payload: "Access denied. Admin privileges required." });
          }
        }
      }
    } catch (error) {
      setError("Network error. Please check your connection and try again.");
      dispatch({ type: "LOGIN_FAILURE", payload: "An error occurred while logging in." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="admin-login-section">
      <Container>
        <Row className="admin-login-container">
          <Col lg="6" md="6">
            <div className="admin-login-img">
              <img src={loginImg} alt="Admin Login" />
            </div>
          </Col>
          <Col lg="6" md="6">
            <div className="admin-login-form">
              <div className="admin-user">
                <img src={userIcon} alt="Admin" />
              </div>
              <h2>Admin Login</h2>
              <p className="admin-login-subtitle">Sign in to access admin dashboard</p>
              
              {error && (
                <div className="admin-error-msg">
                  <i className="ri-error-warning-line"></i> {error}
                </div>
              )}

              <Form onSubmit={handleClick}>
                <FormGroup>
                  <label htmlFor="email">Email Address</label>
                  <div className="input-wrapper">
                    <i className="ri-mail-line input-icon"></i>
                    <input
                      type="email"
                      placeholder="Enter admin email"
                      id="email"
                      value={credentials.email}
                      onChange={handleChange}
                      className={errors.email ? "error" : ""}
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && (
                    <span className="error-message">
                      <i className="ri-error-warning-line"></i> {errors.email}
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <i className="ri-lock-line input-icon"></i>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      id="password"
                      value={credentials.password}
                      onChange={handleChange}
                      className={errors.password ? "error" : ""}
                      autoComplete="current-password"
                    />
                    <span
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </span>
                  </div>
                  {errors.password && (
                    <span className="error-message">
                      <i className="ri-error-warning-line"></i> {errors.password}
                    </span>
                  )}
                </FormGroup>

                <Button type="submit" className="admin-auth-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span> Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </Form>
              <p className="back-to-site">
                <a href="/">
                  <i className="ri-arrow-left-line"></i> Back to Website
                </a>
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default AdminLogin;

